// frontend/src/components/Dashboard/CharacterEditorModal.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { charactersAPI } from '../../services/api';
import SkillsTab from './SkillsTab';
import AbilitiesTab from './AbilitiesTab';
import './CharacterEditorModal.css';

function CharacterEditorModal({ isOpen, characterId, onClose, onUpdated }) {
  const [character, setCharacter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');

  const [stats, setStats] = useState({
    physique: 1,
    coordination: 1,
    logic: 1,
    perception: 1,
    presence: 1,
    resilience: 1
  });

  const [skills, setSkills] = useState({});
  const [abilities, setAbilities] = useState([]);
  const [showBrowser, setShowBrowser] = useState(false); // Track if abilities browser is open

  const safeParseJson = (val, fallback) => {
    try {
      if (val === null || val === undefined) return fallback;
      if (typeof val === 'string') return val ? JSON.parse(val) : fallback;
      if (typeof val === 'object') return val;
      return fallback;
    } catch {
      return fallback;
    }
  };

  const loadCharacter = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await charactersAPI.getCharacter(characterId);
      const char = res.data?.character;

      setCharacter(char);
      setStats({
        physique: char.physique || 1,
        coordination: char.coordination || 1,
        logic: char.logic || 1,
        perception: char.perception || 1,
        presence: char.presence || 1,
        resilience: char.resilience || 1
      });

      // Parse skills if they exist
      try {
        const parsedSkills =
          typeof char.feats === 'string' ? JSON.parse(char.feats) : char.feats || {};
        setSkills(parsedSkills);
      } catch (e) {
        console.warn('Failed to parse skills:', e);
        setSkills({});
      }

      // Parse abilities
      try {
        const parsedAbilities =
          typeof char.forges === 'string' ? JSON.parse(char.forges) : char.forges || {};

        // Convert from object format to array format
        const abilitiesArray = Object.entries(parsedAbilities).map(([name, data]) => ({
          name,
          ...(typeof data === 'object' ? data : {})
        }));

        setAbilities(abilitiesArray);
      } catch (e) {
        console.warn('Failed to parse abilities:', e);
        setAbilities([]);
      }
    } catch (err) {
      console.error('Failed to load character:', err);
      setError(err?.response?.data?.error || 'Failed to load character');
    } finally {
      setIsLoading(false);
    }
  }, [characterId]);

  useEffect(() => {
    if (isOpen && characterId) {
      loadCharacter();
    }
  }, [isOpen, characterId, loadCharacter]);

  // Reset state when modal closes to prevent stale data
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('stats');
      setShowBrowser(false);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeTab !== 'abilities') {
      setShowBrowser(false);
    }
  }, [activeTab]);


  // Baselines (last-saved state) used for delta-cost + preventing refunds in the UI
  const baselineSkills = useMemo(() => safeParseJson(character?.feats, {}), [character]);
  const baselineForges = useMemo(() => safeParseJson(character?.forges, {}), [character]);

  // XP balance source (robust model):
  // - availableXp is the spendable currency
  // - fallback to legacy xp if needed
  const xpBalance = character?.availableXp ?? character?.xp ?? 0;

  // Calculate XP costs (delta-only)
  const calculateStatCost = (currentValue, targetValue) => {
    if (targetValue <= currentValue) return 0;
    let cost = 0;
    for (let i = currentValue + 1; i <= targetValue; i++) {
      cost += (i * i) * 10;
    }
    return cost;
  };

  const calculateTotalStatCost = () => {
    if (!character) return 0;
    let total = 0;
    Object.entries(stats).forEach(([stat, value]) => {
      const original = character[stat] || 1;
      total += calculateStatCost(original, value);
    });
    return total;
  };

  const calculateSkillCost = (currentTier, targetTier) => {
    if (targetTier <= currentTier) return 0;
    let cost = 0;
    for (let i = currentTier + 1; i <= targetTier; i++) {
      if (i === 5) {
        cost += (i * i) * 50; // Legendary tier
      } else {
        cost += (i * i) * 20;
      }
    }
    return cost;
  };

  const calculateTotalSkillCost = () => {
    // DELTA-ONLY: baseline tier -> current tier
    let total = 0;
    Object.entries(skills).forEach(([skillName, skill]) => {
      // Base skill tier cost
      const fromTier = Number(baselineSkills?.[skillName]?.tier ?? 0);
      const toTier = Number(skill?.tier ?? 0);
      total += calculateSkillCost(fromTier, toTier);

      // Specialization tier cost (if exists)
      const currentSpec = skill?.specialization;
      const baselineSpec = baselineSkills?.[skillName]?.specialization;
      
      if (currentSpec && typeof currentSpec === 'object' && currentSpec.name) {
        const fromSpecTier = Number(baselineSpec?.tier ?? 0);
        const toSpecTier = Number(currentSpec.tier ?? 0);
        total += calculateSkillCost(fromSpecTier, toSpecTier);
      }
    });
    return total;
  };

  const calculateTotalAbilitiesCost = () => {
    // DELTA-ONLY: only newly-added abilities cost XP
    const baselineNames = new Set(Object.keys(baselineForges || {}));
    return abilities.reduce((total, ability) => {
      if (!ability?.name) return total;
      if (baselineNames.has(ability.name)) return total; // already saved, no cost
      return total + (Number(ability.xpCost) || 0);
    }, 0);
  };

  const totalStatXP = calculateTotalStatCost();
  const totalSkillXP = calculateTotalSkillCost();
  const totalAbilitiesXP = calculateTotalAbilitiesCost();
  const totalXPSpent = totalStatXP + totalSkillXP + totalAbilitiesXP;

  const availableXP = xpBalance - totalXPSpent;

  // Handle stat changes (prevent refunds in UI: cannot go below saved stat)
  const handleStatChange = (stat, newValue) => {
    const original = character?.[stat] || 1;
    const parsed = parseInt(newValue, 10);
    const clamped = Math.max(1, Math.min(10, Number.isFinite(parsed) ? parsed : original));
    const locked = Math.max(original, clamped); // no decreases below saved
    setStats((prev) => ({ ...prev, [stat]: locked }));
  };

  // Save changes
  const handleSave = async () => {
    if (availableXP < 0) {
      alert('Cannot save: You have spent more XP than available!');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Convert abilities array back to object format for storage
      const abilitiesObject = abilities.reduce((acc, ability) => {
        if (!ability?.name) return acc;
        acc[ability.name] = ability;
        return acc;
      }, {});

      const updateData = {
        physique: stats.physique,
        coordination: stats.coordination,
        logic: stats.logic,
        perception: stats.perception,
        presence: stats.presence,
        resilience: stats.resilience,
        feats: skills,
        forges: abilitiesObject
      };

      await charactersAPI.updateCharacter(characterId, updateData);

      // CRITICAL: Reload character data immediately after saving
      await loadCharacter();

      // Notify parent to refresh
      onUpdated?.();

      // Close modal
      onClose?.();
    } catch (err) {
      console.error('Failed to save character:', err);
      setError(err?.response?.data?.error || 'Failed to save character');
      alert('Error saving character: ' + (err?.response?.data?.error || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleBrowserToggle = (open) => {
    setShowBrowser(open);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={`modal-container character-editor-modal ${showBrowser ? 'expanded' : ''}`}>
        <div className="modal-header">
          <h3>Edit Character: {character?.name || 'Loading...'}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="modal-body">
            <p>Loading character...</p>
          </div>
        ) : error ? (
          <div className="modal-body">
            <div className="error-message">{error}</div>
          </div>
        ) : (
          <>
            <div className="xp-tracker">
              <div className="xp-row">
                <span>XP Available (Balance):</span>
                <span className="xp-value">{xpBalance}</span>
              </div>
              <div className="xp-row">
                <span>XP Pending Spend (Stats):</span>
                <span className="xp-value">-{totalStatXP}</span>
              </div>
              <div className="xp-row">
                <span>XP Pending Spend (Skills):</span>
                <span className="xp-value">-{totalSkillXP}</span>
              </div>
              <div className="xp-row">
                <span>XP Pending Spend (Abilities):</span>
                <span className="xp-value">-{totalAbilitiesXP}</span>
              </div>
              <div className="xp-row total">
                <span>Available XP (After Pending):</span>
                <span className={`xp-value ${availableXP < 0 ? 'negative' : ''}`}>{availableXP}</span>
              </div>
            </div>

            <div className="editor-tabs">
              <button
                className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                Stats
              </button>
              <button
                className={`tab-button ${activeTab === 'skills' ? 'active' : ''}`}
                onClick={() => setActiveTab('skills')}
              >
                Skills
              </button>
              <button
                className={`tab-button ${activeTab === 'abilities' ? 'active' : ''}`}
                onClick={() => setActiveTab('abilities')}
              >
                Abilities
              </button>
            </div>

            <div className="modal-body editor-content">
              {activeTab === 'stats' && (
                <div className="stats-editor">
                  <p className="section-note">
                    <strong>Note:</strong> Stats range from 1-10. Cost to increase: (Target Stat)² × 10 XP
                  </p>

                  <div className="stats-grid">
                    {Object.entries(stats).map(([stat, value]) => {
                      const originalValue = character[stat] || 1;
                      const cost = calculateStatCost(originalValue, value);
                      const statName = stat.charAt(0).toUpperCase() + stat.slice(1);

                      return (
                        <div key={stat} className="stat-editor-box">
                          <h4>{statName}</h4>
                          <div className="stat-controls">
                            <input
                              type="number"
                              min={originalValue} // lock UI to saved
                              max="10"
                              value={value}
                              onChange={(e) => handleStatChange(stat, e.target.value)}
                              className="stat-input"
                            />
                          </div>
                          <div className="stat-info">
                            <span className="stat-modifier">Modifier: +{value - 1}</span>
                            {cost > 0 && <span className="stat-cost">Cost: {cost} XP</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'skills' && (
                <SkillsTab character={character} skills={skills} setSkills={setSkills} availableXP={availableXP} />
              )}

              {activeTab === 'abilities' && (
                <AbilitiesTab
                  character={character}
                  abilities={abilities}
                  setAbilities={setAbilities}
                  availableXP={availableXP}
                  stats={stats}
                  onBrowserToggle={handleBrowserToggle}
                />
              )}
            </div>

            <div className="modal-footer">
              <button onClick={onClose} className="btn-secondary" disabled={isSaving}>
                Cancel
              </button>
              <button onClick={handleSave} className="btn-primary" disabled={isSaving || availableXP < 0}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CharacterEditorModal;